// Apporteurs de partenaires (D-096), d'après les maquettes P1 à P5 :
//  - accueil des adultes (P1) : « Tu connais une entreprise ? », selon la règle de domain/apporteurs
//    (jamais sans formule, la première semaine du mois et au lancement, « Plus tard ») ;
//  - « Proposer une entreprise » (P2) : le lien du club d'abord, avec le message tout prêt au vouvoiement
//    (WhatsApp, Copier, Partager) ; sinon « Donner le contact au bureau », avec l'accord de l'entreprise ;
//    une entreprise déjà partenaire ou déjà en piste est signalée tout de suite ;
//  - la page de l'entreprise (P3, sans compte) : « Recommandé par … », les chiffres du club, les formules ;
//  - le bureau (P4) : « Trouver un partenaire », les pistes (l'apporteur, la provenance, les quatre états), l'offre ;
//  - « Trouver un partenaire » (P5 ; nom unique depuis D-101) : mes pistes, et les ambassadeurs de la saison.
// Un compte partenaire (D-037) apporte aussi, au vouvoiement ; ses missions Ambassade comptent comme celles des membres (D-100).
import './apporteurs.css';
import { consentNote, dataHref, legalFootLink } from './legal-links';
import {
  ambassadors, clubPacks, clubProgram, deletePack, leadsAdmin, myLeads, offerRequest, partnerOffer, refCheck, refGiveContact, refHome,
  refLink, savePack, saveProgram, setLeadState,
  type AdminLead, type Ambassador, type MyLead, type Offer, type Pack, type Program, type RefCheck, type RefHome,
} from '../lib/apporteurs';
import { trackPartners } from '../lib/api';
import {
  LEAD_LABEL, LEAD_STATUSES, MY_LEAD_CHIP, REF_MISSIONS, leadOrigin, myLeadLine, offerNumbers, offerPitch, packPrice, refMessage, refUrl,
  seasonLine, showReferralCard, typo, type LeadStatus,
} from '../domain/apporteurs';
import { ofClub } from '../domain/derogation';
import { localDate } from '../domain/schedule';
import { isMinorAccount, personaOf, type Ctx } from './context';
import { vouvoie } from './vous';
import { $, $$, armed, html, mount, openSheet, toast, type Html } from './dom';
import { shortDate } from './format';
import { openShare, since } from './share';
import { applyBrand } from './theme';

/** Tutoiement dans l'app ; vouvoiement pour un compte partenaire (une entreprise), même dans sa casquette Bénévole (D-103). */
const tv = (vous: boolean) => (tu: string, v: string) => (vous ? v : tu);
const isPartnerView = vouvoie;
const today = () => localDate(new Date());
/** « hier », « il y a 5 jours » ; au-delà d'une semaine, la date. */
const when = (iso: string) => (Date.now() - Date.parse(iso) < 7 * 86_400_000 ? since(iso).replace(/ (h|min|jours)$/, '\u00a0$1')
  : shortDate(localDate(new Date(iso))));
const mark = (p: { name: string; logo: string | null }, cls: string): Html => (p.logo
  ? html`<img class="${cls}" src="${p.logo}" alt="Logo de ${p.name}">`
  : html`<span class="${cls} ap-mono" aria-hidden="true">${p.name.split(/\s+/).filter(Boolean).slice(0, 3).map(w => w[0]!.toUpperCase()).join('')}</span>`);

// « Plus tard » et « entreprise proposée » : la carte se range jusqu'à la prochaine fenêtre (cet appareil).
const SNOOZE = (ctx: Ctx) => `arceau.apporteur.${ctx.uid}.${ctx.club?.id ?? ''}`;
const snoozedOn = (ctx: Ctx): string | null => { try { return localStorage.getItem(SNOOZE(ctx)); } catch { return null; } };
const snooze = (ctx: Ctx) => { try { localStorage.setItem(SNOOZE(ctx), today()); } catch { /* navigation privée */ } };

// -----------------------------------------------------------------------------
// Accueil (P1)
// -----------------------------------------------------------------------------
/** La carte « Tu connais une entreprise ? » (adultes, comptes partenaires) ; rien sinon. */
export async function renderReferralCard(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!ctx.club || isMinorAccount(ctx)) return;
  const h = await refHome(ctx.club.id).catch(() => null);
  if (!h?.can || !showReferralCard(today(), h.packs_since ?? null, snoozedOn(ctx))) { mount(el, html``); return; }
  const vous = isPartnerView(ctx), t = tv(vous);
  mount(el, html`
    <section class="block ap-card" aria-label="Le club cherche des partenaires">
      <p class="eyebrow">Le club cherche des partenaires</p>
      <h3>${t('Tu connais une entreprise ?', 'Vous connaissez une entreprise ?')}</h3>
      <p class="ap-text">${t('Ton employeur, un artisan, le commerce du quartier… Présente-lui le club en un message : le bureau s’occupe du reste.',
        'Un client, un fournisseur, une entreprise voisine… Présentez-lui le club en un message : le bureau s’occupe du reste.')}</p>
      <div class="ap-gain"><span>Une mission Ambassade tout de suite</span><span>${REF_MISSIONS.signed} de plus et un cadeau du club s’il signe</span></div>
      <button type="button" class="btn btn-primary" data-ap-propose>Proposer une entreprise</button>
      <p class="hint ap-foot"><span>${seasonLine(h.season?.leads ?? 0, h.season?.signed ?? 0, vous)}</span>
        <span class="ap-foot-links">${h.mine?.leads ? html`<a href="#/pistes">${t('Mes pistes', 'Vos pistes')} (${h.mine.leads}) ›</a>` : ''}
        <button type="button" class="ap-later" data-ap-later>Plus tard</button></span></p>
    </section>`);
  $(el, '[data-ap-propose]')!.addEventListener('click', () => void openPropose(ctx, () => mount(el, html``)));
  $(el, '[data-ap-later]')!.addEventListener('click', () => { snooze(ctx); mount(el, html``); });
}

// -----------------------------------------------------------------------------
// Proposer une entreprise (P2)
// -----------------------------------------------------------------------------
export async function openPropose(ctx: Ctx, onDone?: () => void): Promise<void> {
  const club = ctx.club;
  if (!club) return;
  let h: RefHome, token: string;
  try { [h, token] = await Promise.all([refHome(club.id), refLink(club.id)]); }
  catch { toast('Impossible pour le moment. Réessaie.', 'ko'); return; }
  if (!h.can) { toast('Réservé aux adultes du club.', 'ko'); return; }
  const t = tv(isPartnerView(ctx));
  const message = refMessage({ clubName: club.name, intro: h.me?.intro ?? null, members: h.members ?? 0, url: refUrl(location.origin, token) });
  openShare('Proposer une entreprise', html`
    <div class="ap-way on"><span class="ap-tag">Conseillé</span><strong>Envoyer le lien du club</strong>
      <small>L’entreprise découvre ce que le club propose et laisse elle-même ses coordonnées.</small></div>`, message, {
    footer: html`<button type="button" class="ap-way" id="ap-contact"><strong>Donner le contact au bureau</strong>
      <small>${t('Nom de l’entreprise, la personne, comment tu la connais ; seulement si elle est d’accord pour être contactée.',
        'Nom de l’entreprise, la personne, comment vous la connaissez ; seulement si elle est d’accord pour être contactée.')}</small></button>`,
    setup: (sheet, close) => {
      sheet.classList.add('ap-sheet');
      const ta = $<HTMLTextAreaElement>(sheet, '#iv-msg');
      if (ta) ta.rows = 6;
      // Le message parti (ou copié) : la carte d'accueil se range jusqu'à la prochaine fenêtre.
      for (const b of $$(sheet, '#iv-wa, #iv-copy, #iv-share')) b.addEventListener('click', () => { snooze(ctx); onDone?.(); });
      $(sheet, '#ap-contact')!.addEventListener('click', () => { close(); openContact(ctx, onDone); });
    },
  });
}

const DUP_TEXT = (vous: boolean): Record<Exclude<RefCheck, null>, string> => ({
  partenaire: 'Déjà partenaire du club : merci d’y avoir pensé !',
  piste: 'Déjà en piste au club : le bureau s’en occupe.',
  mienne: vous ? 'Vous l’avez déjà proposée : le bureau s’en occupe.' : 'Tu l’as déjà proposée : suis-la dans « Trouver un partenaire ».',
});

function openContact(ctx: Ctx, onDone?: () => void) {
  const club = ctx.club!;
  const vous = isPartnerView(ctx), t = tv(vous);
  openSheet('Donner le contact au bureau', html`
    <form class="form" id="f-ap-contact" novalidate>
      <p class="muted small">${t('Le bureau la contacte de ta part. Seulement si elle est d’accord ; sinon, envoie-lui plutôt le lien du club.',
        'Le bureau la contacte de votre part. Seulement si elle est d’accord ; sinon, envoyez-lui plutôt le lien du club.')}</p>
      <label class="field"><span>Entreprise</span><input name="company" maxlength="80" autocomplete="organization" required></label>
      <p class="ap-dup" id="ap-dup" role="status" hidden></p>
      <label class="field"><span>La personne à contacter</span><input name="contact_name" maxlength="80" placeholder="ex. Sophie, la gérante"></label>
      <div class="row-2">
        <label class="field"><span>Téléphone</span><input name="phone" type="tel" inputmode="tel" maxlength="20"></label>
        <label class="field"><span>E-mail</span><input name="email" type="email" inputmode="email" maxlength="120"></label>
      </div>
      <label class="field"><span>${t('Comment tu la connais (facultatif)', 'Comment vous la connaissez (facultatif)')}</span>
        <input name="relation" maxlength="140" placeholder="${t('ex. mon employeur, ma boulangère', 'ex. un client, notre fournisseur')}"></label>
      <label class="check"><input type="checkbox" name="consent"><span>Elle est d’accord pour être contactée par le club.</span></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Donner au bureau</button>
      <button type="button" class="btn-link-accent" id="ap-back">‹ Envoyer plutôt le lien du club</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-ap-contact')!;
    const msg = (s: string) => { $(f, '.form-msg')!.textContent = s; };
    const dupEl = $<HTMLElement>(sheet, '#ap-dup')!;
    const company = $<HTMLInputElement>(f, 'input[name=company]')!;
    let dup: RefCheck = null, seq = 0, timer = 0;
    const showDup = (d: RefCheck) => { dup = d; dupEl.hidden = !d; dupEl.textContent = d ? DUP_TEXT(vous)[d] : ''; };
    const check = async () => {
      const name = company.value.trim(), n = ++seq;
      if (name.length < 3) { showDup(null); return; }
      const d = await refCheck(club.id, name).catch(() => null);
      if (n === seq) showDup(d);
    };
    company.addEventListener('input', () => { clearTimeout(timer); timer = window.setTimeout(() => void check(), 450); });
    company.addEventListener('blur', () => void check());
    $(sheet, '#ap-back')!.addEventListener('click', () => { close(); void openPropose(ctx, onDone); });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg('');
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      if (!v('company')) { msg('Indique le nom de l’entreprise.'); return; }
      if (dup) { msg(DUP_TEXT(vous)[dup]); return; }
      if (!v('phone') && !v('email')) { msg(t('Laisse un téléphone ou un e-mail pour que le bureau puisse la joindre.', 'Laissez un téléphone ou un e-mail pour que le bureau puisse la joindre.')); return; }
      if (v('email') && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v('email'))) { msg('Adresse e-mail invalide.'); return; }
      if (v('phone') && !/^[0-9 +().-]{6,20}$/.test(v('phone'))) { msg('Numéro de téléphone invalide.'); return; }
      if (d.get('consent') !== 'on') { msg(t('Seulement si elle est d’accord : sinon, envoie-lui plutôt le lien du club.', 'Seulement si elle est d’accord : sinon, envoyez-lui plutôt le lien du club.')); return; }
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true;
      try {
        const r = await refGiveContact(club.id, { company: v('company'), contact_name: v('contact_name') || null, email: v('email') || null,
          phone: v('phone') || null, relation: v('relation') || null });
        snooze(ctx);
        close();
        toast(r.points ? 'Merci ! Une mission Ambassade : le bureau prend le relais.' : 'Merci ! Le bureau prend le relais.');
        onDone?.();
      } catch (err) {
        btn.disabled = false;
        const { code, hint } = err as { code?: string; hint?: string };
        if (code === '23505') { showDup((hint as RefCheck) || 'piste'); msg(DUP_TEXT(vous)[(hint as Exclude<RefCheck, null>) || 'piste']); }
        else msg(code === '54000' ? 'Cinq contacts par jour au plus : la suite demain.' : code === '42501' ? 'Réservé aux adultes du club.' : 'Envoi impossible. Réessaie.');
      }
    });
  });
}

// -----------------------------------------------------------------------------
// Trouver un partenaire : mes pistes (P5)
// -----------------------------------------------------------------------------
export async function renderMyLeads(root: HTMLElement, ctx: Ctx): Promise<void> {
  const club = ctx.club;
  if (!club || isMinorAccount(ctx)) { mount(root, html`<div class="empty"><p class="empty-title">Rien à afficher.</p></div>`); return; }
  const [h, list, amb] = await Promise.all([
    refHome(club.id), myLeads(club.id).catch(() => [] as MyLead[]), ambassadors(club.id).catch(() => [] as Ambassador[]),
  ]);
  if (!h.can) { mount(root, html`<div class="empty"><p class="empty-title">Réservé aux adultes du club.</p></div>`); return; }
  const vous = isPartnerView(ctx), t = tv(vous);
  const chip = (l: MyLead) => (l.mine && l.status
    ? html`<span class="chip ${MY_LEAD_CHIP[l.status].cls}">${MY_LEAD_CHIP[l.status].text}</span>`
    : html`<span class="chip chip-mute">déjà au club</span>`);
  mount(root, html`
    <section class="screen ap-mine">
      <a class="back" href="${personaOf(ctx) === 'partenaire' ? '#/' : '#/club'}" data-back>‹ ${personaOf(ctx) === 'partenaire' ? 'Accueil' : 'Le club'}</a>
      <header class="screen-head"><p class="eyebrow">${t('Mes pistes', 'Vos pistes')} · ${club.name}</p><h2>Trouver un partenaire</h2></header>
      <section class="block ap-list">
        ${list.length ? list.map(l => html`
          <div class="ap-row"><strong>${l.company}</strong>${chip(l)}<small>${myLeadLine(l)}</small></div>`)
          : html`<p class="muted ap-empty">${t('Aucune piste pour l’instant. Ton employeur, un artisan, le commerce du quartier : le club cherche des partenaires.',
            'Aucune piste pour l’instant. Un client, un fournisseur, une entreprise voisine : le club cherche des partenaires.')}</p>`}
      </section>
      <button type="button" class="btn btn-primary" id="ap-propose">${list.length ? 'Proposer une autre entreprise' : 'Proposer une entreprise'}</button>
      ${h.mine?.signed ? html`<p class="ap-amb"><span class="ap-by">★ Ambassade</span> ${t('Merci : grâce à toi, le club a un partenaire de plus.', 'Merci : grâce à vous, le club a un partenaire de plus.')}</p>` : ''}
      <section class="block">
        <div class="block-head"><h3>Ambassadeurs de la saison</h3>
          <span class="mt-count">${h.season?.signed ?? 0} ${(h.season?.signed ?? 0) > 1 ? 'partenaires apportés' : 'partenaire apporté'}</span></div>
        ${amb.length ? html`<ol class="ap-rank">${amb.map((a, i) => html`
          <li class="${a.me ? 'me' : ''}"><b>${i + 1}</b><span>${a.me ? t('Toi', 'Vous') : a.name}</span><b>${a.signed}</b></li>`)}</ol>`
          : html`<p class="muted small">Aucun partenaire signé cette saison : ${t('à toi d’ouvrir le score.', 'à vous d’ouvrir le score.')}</p>`}
      </section>
      <details class="alt-box">
        <summary>Comment ça marche</summary>
        <ul class="vp-rules">
          <li>${t('Envoie le lien du club', 'Envoyez le lien du club')} : l’entreprise découvre ce que le club propose et laisse elle-même ses coordonnées.</li>
          <li>${t('Ou donne son contact au bureau', 'Ou donnez son contact au bureau')}, seulement si elle est d’accord pour être contactée.</li>
          <li>Une mission Ambassade à la piste, ${REF_MISSIONS.signed} de plus si elle signe${h.gift ? `, et un cadeau du club : ${h.gift}` : ', et un cadeau du club'}. Une fois par entreprise ; jamais d’argent.</li>
          <li>La première personne qui apporte une entreprise la garde 6 mois.</li>
          <li>Le bureau ${t('te', 'vous')} prévient à chaque étape.</li>
        </ul>
      </details>
    </section>`);
  $(root, '#ap-propose')!.addEventListener('click', () => void openPropose(ctx, () => void renderMyLeads(root, ctx)));
}

// -----------------------------------------------------------------------------
// Le bureau (P4) : les pistes, puis l'offre aux entreprises
// -----------------------------------------------------------------------------
export async function renderLeadsAdmin(el: HTMLElement, clubId: string): Promise<void> {
  const leads = await leadsAdmin(clubId).catch(() => [] as AdminLead[]);
  const late = leads.filter(l => l.late).length;
  const row = (l: AdminLead) => html`
    <div class="ap-row ${l.late ? 'ap-late' : ''}">
      <strong>${l.company}</strong>
      ${l.referrer ? html`<span class="ap-by">★ ${l.referrer}</span>` : html`<span class="chip chip-mute">page du club</span>`}
      <small>${leadOrigin(l)} · ${when(l.created_at)}${l.late ? ' · à rappeler' : ''}</small>
      ${l.dup ? html`<small class="ce-warn">${l.dup === 'partenaire' ? 'Déjà partenaire du club' : 'Déjà en piste au club'}${l.via ? ` · arrivée par le lien de ${l.via}` : ''}</small>` : ''}
      ${l.phone || l.email ? html`<small class="ap-reach">${l.phone ? html`<a href="tel:${l.phone.replace(/[^0-9+]/g, '')}">${l.phone}</a>` : ''}${l.email ? html`<a href="mailto:${l.email}">${l.email}</a>` : ''}</small>` : ''}
      ${l.pack ? html`<small>Formule qui l’intéresse : ${l.pack}</small>` : ''}
      ${l.message ? html`<small class="ap-msg">« ${l.message} »</small>` : ''}
      <span class="ap-st" role="radiogroup" aria-label="Où en est la piste ${l.company}">${LEAD_STATUSES.map(s => {
        const on = s === l.status;
        return html`<button type="button" role="radio" aria-checked="${String(on)}" class="${on ? `on${s === 'signe' ? ' ok' : ''}` : ''}"
          data-ap-lead="${l.id}" data-st="${s}">${on && s === 'signe' ? '✓ Signé' : LEAD_LABEL[s]}</button>`;
      })}</span>
    </div>`;
  mount(el, html`
    <section class="block ap-leads">
      <div class="block-head"><h3>Trouver un partenaire · ${leads.length}</h3>${late ? html`<span class="chip chip-warn">${late} à rappeler</span>` : ''}</div>
      <p class="muted small">Les entreprises proposées par les membres du club ; chacun suit l’avancement de sa piste. Une piste « Nouveau » depuis 7 jours te revient dans la cloche.</p>
      ${leads.length ? leads.map(row) : html`<p class="muted small ap-empty">Aucune piste pour l’instant : les membres proposent des entreprises depuis l’accueil et leur page « Moi ».</p>`}
    </section>`);
  for (const b of $$<HTMLButtonElement>(el, '[data-ap-lead]')) {
    b.addEventListener('click', async () => {
      const lead = leads.find(l => l.id === b.dataset.apLead);
      const st = b.dataset.st as LeadStatus;
      if (!lead || st === lead.status) return;
      // « Signé » compte 3 missions Ambassade, prévient l'apporteur et crée la fiche partenaire : deux gestes.
      if (st === 'signe' && !armed(b, 'Confirmer ?')) return;
      try {
        await setLeadState(lead.id, st);
        toast(st === 'signe' ? `${lead.company} : signé, fiche partenaire créée (à compléter : logo, offre)` : `${lead.company} : ${LEAD_LABEL[st].toLowerCase()}`);
        await renderLeadsAdmin(el, clubId);
      }
      catch { toast('Mise à jour impossible.', 'ko'); }
    });
  }
}

/** Ce que l'offre aux entreprises lit (les formules, le programme des apporteurs, le lien de la page). */
export interface OfferAdmin { packs: Pack[]; prog: Program | null; token: string | null }
export async function offerAdminData(clubId: string): Promise<OfferAdmin> {
  const [packs, prog, token] = await Promise.all([
    clubPacks(clubId).catch(() => [] as Pack[]), clubProgram(clubId).catch(() => null), refLink(clubId).catch(() => null),
  ]);
  return { packs, prog, token };
}

/** Réglages · Partenaires, rubrique « L'offre aux entreprises » (D-139) : les formules, la page que voit l'entreprise. */
export function renderPacksAdmin(el: HTMLElement, clubId: string, d: OfferAdmin, again: () => void): void {
  const { packs, token } = d;
  mount(el, html`
    <div class="ap-offer nv-stack">
      <p class="muted small">Ce que voit une entreprise qui ouvre le lien d’un membre : les chiffres du club, calculés tout seuls, et tes formules.
        ${packs.length ? '' : html`<strong>Sans formule, la carte « Tu connais une entreprise ? » ne paraît pas à l’accueil des membres.</strong>`}</p>
      ${packs.map(p => html`
        <button type="button" class="ap-pack ap-pack-edit" data-ap-pack="${p.id}"><b>${p.name}</b><em>${packPrice(p.price_cents)}</em>
          ${p.content ? html`<small>${p.content}</small>` : ''}</button>`)}
      <span class="btn-row">${packs.length < 8 ? html`<button type="button" class="btn btn-ghost btn-sm ap-add" id="ap-pack-new">+ Formule</button>` : ''}
        ${token ? html`<a class="btn btn-ghost btn-sm" href="${refUrl(location.origin, token)}" target="_blank" rel="noopener">Voir la page ↗</a>` : ''}</span>
    </div>`);
  $(el, '#ap-pack-new')?.addEventListener('click', () => openPackEditor(clubId, null, packs.length, again));
  for (const b of $$<HTMLButtonElement>(el, '[data-ap-pack]')) {
    b.addEventListener('click', () => openPackEditor(clubId, packs.find(p => p.id === b.dataset.apPack) ?? null, packs.length, again));
  }
}

/** Le cadeau de l'apporteur, puis l'app offerte par un partenaire (deux rubriques, un même programme enregistré). */
export function renderProgramAdmin(el: HTMLElement, clubId: string, d: OfferAdmin, part: 'gift' | 'app', again: () => void): void {
  const prog = d.prog;
  mount(el, html`
    <form class="form ap-prog" id="f-ap-prog-${part}" novalidate>
      ${part === 'gift' ? html`
        <label class="field"><span>Le cadeau du club à l’apporteur d’un partenaire signé</span>
          <input name="gift" maxlength="120" value="${prog?.gift ?? ''}" placeholder="ex. invitation au repas des partenaires"></label>
        <p class="hint">Jamais d’argent pour l’apporteur : des missions Ambassade (${REF_MISSIONS.piste} à la piste, ${REF_MISSIONS.signed} à la signature) et ce cadeau.</p>`
      : html`
        <label class="check"><input type="checkbox" name="app_option" ${prog?.app_option ? 'checked' : ''}>
          <span><strong>Proposer « l’app du club offerte par » votre entreprise</strong>
          <span class="muted small">partenaire officiel de l’app : une entreprise par saison</span></span></label>
        <label class="field"><span>Prix demandé pour l’app (€, facultatif)</span>
          <input name="app_price" type="number" inputmode="decimal" min="1" step="1" value="${prog?.app_price_cents != null ? prog.app_price_cents / 100 : ''}"></label>`}
      <p class="form-msg" role="alert"></p>
      <button type="submit" class="btn btn-ghost btn-sm">Enregistrer</button>
    </form>`);
  const f = $<HTMLFormElement>(el, `#f-ap-prog-${part}`)!;
  f.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(f);
    const v = (k: string) => String(fd.get(k) ?? '').trim();
    const price = part === 'app' ? (v('app_price') ? Math.round(Number(v('app_price').replace(',', '.')) * 100) : null) : prog?.app_price_cents ?? null;
    if (price != null && (Number.isNaN(price) || price < 100)) { $(f, '.form-msg')!.textContent = 'Prix de l’app : 1 € au moins, ou rien.'; return; }
    try {
      await saveProgram({ club_id: clubId,
        gift: part === 'gift' ? v('gift') || null : prog?.gift ?? null,
        app_option: part === 'app' ? fd.get('app_option') === 'on' : !!prog?.app_option, app_price_cents: price });
      toast(part === 'gift' ? 'Cadeau de l’apporteur enregistré' : 'Offre de l’app enregistrée');
      again();
    } catch { $(f, '.form-msg')!.textContent = 'Enregistrement impossible. Réessaie.'; }
  });
}

function openPackEditor(clubId: string, p: Pack | null, count: number, onClosed: () => void) {
  openSheet(p ? p.name : 'Nouvelle formule', html`
    <form class="form" id="f-ap-pack" novalidate>
      <label class="field"><span>Nom</span><input name="name" maxlength="40" value="${p?.name ?? ''}" placeholder="Bronze, Argent, Maillot…" required></label>
      <label class="field"><span>Prix pour la saison (€)</span><input name="price" type="number" inputmode="decimal" min="1" step="1"
        value="${p?.price_cents != null ? p.price_cents / 100 : ''}" placeholder="vide : sur devis"></label>
      <label class="field"><span>Ce que l’entreprise reçoit</span><textarea name="content" rows="3" maxlength="240"
        placeholder="Votre logo sur la page du club et dans l’app, une offre pour les licenciés.">${p?.content ?? ''}</textarea></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">${p ? 'Enregistrer' : 'Ajouter la formule'}</button>
      ${p ? html`<button type="button" class="btn btn-danger-ghost" id="ap-pack-del">Retirer cette formule</button>` : ''}
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-ap-pack')!;
    const msg = (s: string) => { $(f, '.form-msg')!.textContent = s; };
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      if (!v('name')) { msg('Indique le nom de la formule.'); return; }
      const price = v('price') ? Math.round(Number(v('price').replace(',', '.')) * 100) : null;
      if (price != null && (Number.isNaN(price) || price < 100)) { msg('Prix : 1 € au moins, ou vide (sur devis).'); return; }
      try {
        await savePack({ id: p?.id, club_id: clubId, name: v('name'), price_cents: price, content: v('content') || null, position: p?.position ?? count });
        toast(p ? 'Formule à jour' : 'Formule ajoutée'); close();
      } catch { msg('Enregistrement impossible (huit formules au plus).'); }
    });
    $(sheet, '#ap-pack-del')?.addEventListener('click', async (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      if (!p || !armed(b, `Confirmer : retirer ${p.name}`)) return;
      try { await deletePack(p.id); toast('Formule retirée'); close(); } catch { msg('Impossible pour le moment.'); }
    });
  }, onClosed);
}

// -----------------------------------------------------------------------------
// La page de l'entreprise (P3) : /partenaires/<jeton>, /c/<adresse>/partenaires, sans compte. Un club sans formule :
// « Ce que le club propose », tarifs sur demande (D-103).
// -----------------------------------------------------------------------------
export async function renderPartnerOffer(root: HTMLElement, slug: string | null, token: string | null, onClubHost = false): Promise<void> {
  let o: Offer | null = null;
  try { o = await partnerOffer(slug, token); } catch { /* réseau : même message */ }
  if (!o) {
    document.title = 'Page introuvable';
    mount(root, html`
      <main class="pub-missing">
        <h1>Page introuvable</h1>
        <p>Ce lien n’est plus valable. Demandez-en un nouveau à la personne qui vous l’a envoyé.</p>
      </main>`);
    return;
  }
  const offer = o;
  const c = offer.club;
  applyBrand({ id: `offer:${slug ?? token}`, name: c.name, short_name: c.short_name, logo_url: c.logo, primary_color: c.color, secondary_color: c.color2 }, false);
  document.title = `Partenariats · ${c.short_name || c.name}`;
  const nums = offerNumbers(offer.numbers);
  // Sur le sous-domaine du club (D-032), sa page publique est à la racine et l'espace membres en /app.
  const publicHref = c.public ? (onClubHost ? '/' : `/c/${c.public}`) : null;
  mount(root, html`
    <div class="ap-pub">
      <header class="ap-pub-head">
        <div class="ap-pub-id">${mark({ name: c.short_name || c.name, logo: c.logo }, 'ap-pub-logo')}
          <p class="ap-kicker">Partenariats${offer.season ? html` · <span class="ap-nw">saison ${offer.season}</span>` : ''}</p></div>
        <h1>Devenez partenaire ${ofClub(c.name)}</h1>
        <p>${offerPitch(offer.numbers.members)}</p>
        ${offer.recommender ? html`<span class="ap-reco">★ Recommandé par ${offer.recommender.first}, ${typo(offer.recommender.role)}</span>` : ''}
      </header>
      <main class="ap-pub-main">
        ${nums.length ? html`<div class="ap-nums">${nums.map(n => html`<div><b>${n.value}</b><span>${n.label}</span></div>`)}</div>` : ''}
        ${offer.packs.length || offer.app ? html`<p class="eyebrow ap-eyebrow">Les formules</p>` : html`
          <p class="eyebrow ap-eyebrow">Ce que le club propose</p>
          <div class="ap-pack ap-pack-default"><b>Votre entreprise aux côtés du club</b><em>Tarifs sur demande</em>
            <ul class="ap-offer-list">
              <li>Votre logo sur la page du club, « Ils nous soutiennent »</li>
              <li>Votre bannière sur l’accueil de l’app des familles du club</li>
              <li>Une offre réservée aux licenciés et à leurs familles</li>
              <li>Un match à domicile « présenté par » votre entreprise</li>
            </ul></div>`}
        ${offer.packs.map(p => html`
          <div class="ap-pack"><b>${p.name}</b><em>${packPrice(p.price_cents)}</em>${p.content ? html`<small>${p.content}</small>` : ''}</div>`)}
        ${offer.app ? html`
          <div class="ap-pack ap-pack-app"><b>L’app du club offerte par votre entreprise</b><em>${packPrice(offer.app.price_cents)}</em>
            <small>Votre logo sur l’accueil de l’app de chaque membre et sur la page du club, toute la saison : une seule entreprise par saison.</small></div>` : ''}
        <button type="button" class="btn btn-primary ap-cta" data-ap-ask>Être recontacté par le club</button>
        <p class="hint ap-center">Le bureau du club vous rappelle. Aucun engagement.</p>
        ${offer.official ? html`<p class="ap-official">L’app du club est offerte par <strong>${offer.official.name}</strong></p>` : ''}
        ${offer.partners.length ? html`
          <section class="ap-sec">
            <p class="eyebrow ap-eyebrow">Ils soutiennent déjà le club</p>
            <div class="pub-partners">${offer.partners.map(pa => html`
              ${pa.website ? html`<a class="pub-partner" href="${pa.website}" target="_blank" rel="noopener" data-pclick="${pa.id}" title="${pa.name}">`
                : html`<span class="pub-partner" title="${pa.name}">`}
                ${pa.logo ? html`<img src="${pa.logo}" alt="${pa.name}">` : html`<b>${pa.name}</b>`}
              ${pa.website ? html`</a>` : html`</span>`}`)}</div>
          </section>` : ''}
      </main>
      <footer class="pub-foot">
        ${mark({ name: c.short_name || c.name, logo: c.logo }, 'pub-logo')}
        <div><strong>${c.name}</strong><small>Partenariats · propulsé par Arceau</small>${legalFootLink(offerDataHref(slug, token, c.public))}</div>
        ${publicHref ? html`<a class="pub-members" href="${publicHref}">Le club</a>` : html`<a class="pub-members" href="${onClubHost ? '/app' : '/'}">Espace membres</a>`}
      </footer>
    </div>`);
  for (const b of $$<HTMLButtonElement>(root, '[data-ap-ask]')) b.addEventListener('click', () => openOfferForm(slug, token, offer));
  // Visibilité des partenaires (D-033) : un affichage par page vue, un clic par lien suivi.
  trackPartners(offer.partners.map(pa => pa.id), 'public');
  for (const a of $$<HTMLAnchorElement>(root, '[data-pclick]')) a.addEventListener('click', () => trackPartners([a.dataset.pclick!], 'public', true));
}

/** La page des données du club (D-103) depuis la page partenaires : par son adresse, sinon par le lien de l'apporteur. */
const offerDataHref = (slug: string | null, token: string | null, pub: string | null | undefined) =>
  dataHref(slug || pub ? { slug: slug || pub } : { via: token ? `/partenaires/${token}` : null });

function openOfferForm(slug: string | null, token: string | null, o: Offer) {
  openSheet(`Devenir partenaire · ${o.club.short_name || o.club.name}`, html`
    <form class="form" id="f-ap-offer" novalidate>
      <p class="muted">Laissez vos coordonnées : le bureau du club vous rappelle pour en parler. Aucun engagement.</p>
      <label class="field"><span>Entreprise ou organisme</span><input name="company" maxlength="80" autocomplete="organization" required></label>
      <label class="field"><span>Votre nom</span><input name="contact_name" maxlength="80" autocomplete="name"></label>
      <div class="row-2">
        <label class="field"><span>Téléphone</span><input name="phone" type="tel" inputmode="tel" maxlength="20" autocomplete="tel"></label>
        <label class="field"><span>E-mail</span><input name="email" type="email" inputmode="email" maxlength="120" autocomplete="email"></label>
      </div>
      ${o.packs.length || o.app ? html`<label class="field"><span>Formule qui vous intéresse</span><select name="pack">
        <option value="">Je ne sais pas encore</option>${o.packs.map(p => html`<option value="${p.id}">${p.name} · ${packPrice(p.price_cents)}</option>`)}
        ${o.app ? html`<option value="app">L’app du club offerte par votre entreprise</option>` : ''}</select></label>` : ''}
      <label class="field"><span>Un mot (facultatif)</span><textarea name="message" rows="3" maxlength="400" placeholder="ex. plutôt le soir, intéressé par un match présenté"></textarea></label>
      <label class="pub-hp" aria-hidden="true">Ne pas remplir <input name="website" tabindex="-1" autocomplete="off"></label>
      <label class="check"><input type="checkbox" name="consent"><span>J’accepte que le club me recontacte au sujet de ce partenariat. Ces coordonnées ne servent qu’à ça.</span></label>
      ${consentNote(offerDataHref(slug, token, o.club.public))}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Être recontacté</button>
    </form>`, (sheet) => {
    const f = $<HTMLFormElement>(sheet, '#f-ap-offer')!;
    const msg = (s: string) => { $(f, '.form-msg')!.textContent = s; };
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      msg('');
      const d = new FormData(f);
      const v = (k: string) => String(d.get(k) ?? '').trim();
      const done = () => mount(sheet.querySelector('.sheet-body')!, html`
        <div class="pub-thanks"><p class="pub-kicker">Demande envoyée</p><h3>Merci !</h3>
        <p>Le bureau du club a bien reçu vos coordonnées et vous rappelle.${o.recommender ? ` Merci à ${o.recommender.first} pour la mise en relation !` : ''}</p></div>`);
      if (v('website')) { done(); return; }   // robot : on ne dit rien
      if (!v('company')) { msg('Indiquez le nom de l’entreprise ou de l’organisme.'); return; }
      if (!v('email') && !v('phone')) { msg('Laissez un téléphone ou un e-mail pour être recontacté.'); return; }
      if (v('email') && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v('email'))) { msg('Adresse e-mail invalide.'); return; }
      if (v('phone') && !/^[0-9 +().-]{6,20}$/.test(v('phone'))) { msg('Numéro de téléphone invalide.'); return; }
      if (d.get('consent') !== 'on') { msg('Cochez la case pour que le club puisse vous recontacter.'); return; }
      const pack = v('pack');
      const message = [pack === 'app' ? 'Intéressé par : l’app du club offerte par notre entreprise.' : '', v('message')].filter(Boolean).join(' ') || null;
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true;
      try {
        await offerRequest(slug, token, { company: v('company'), contact_name: v('contact_name') || null, email: v('email') || null,
          phone: v('phone') || null, message, pack: pack && pack !== 'app' ? pack : null });
        done();
      } catch (err) {
        btn.disabled = false;
        msg((err as { code?: string }).code === '54000' ? 'Trop de demandes pour le moment : réessayez demain.' : 'Envoi impossible. Vérifiez votre connexion et réessayez.');
      }
    });
  });
}
